import { useCallback, useMemo, useState } from "react"
import { useRefreshPassport } from "@mezo-org/passport"
import { PayloadAction } from "@reduxjs/toolkit"

import {
  closeLoan,
  repayDebtAndWithdrawCollateral,
  repayPartialDebt,
  selectGasCompensation,
  selectMinimumNetDebt,
  selectPartialRepayStrategy,
  selectRepayLoanAmount,
  selectRepayStrategy,
  selectUserLoan,
  selectWalletBalances,
  setRepayLoanAmount,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  BorrowPartialRepayStrategy,
  BorrowRepayPosthogEventProps,
  BorrowRepayStrategy,
} from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  normalizeDecimalNumberInput,
  separateThousandsWithComma,
  trimDecimals,
} from "#/shared/utils"

import { usePosthogRepayEvent } from "../posthog-events"

import { useCollateralizationRatioForExistingLoan } from "./borrow-calculation-helpers"
import { useFetchAllBorrowData } from "./borrow-system"
import { useUpdatedCollateralBasedOnRatio } from "./borrow-update-loan"

export const useRepayBorrowAmount = () => {
  const dispatch = useDappDispatch()
  const userLoan = useDappSelector(selectUserLoan)
  const { MUSD } = useDappSelector(selectWalletBalances)
  const repayLoanAmount = useDappSelector(selectRepayLoanAmount)
  const gasCompensation = useDappSelector(selectGasCompensation)
  const minNetDebt = useDappSelector(selectMinimumNetDebt)

  const fullRepaymentAmount = useMemo(
    () => (userLoan?.debtAmount ?? 0n) - (gasCompensation ?? 0n),
    [userLoan?.debtAmount, gasCompensation],
  )

  const maxRepaymentAmount = useMemo(
    () =>
      (userLoan?.debtAmount ?? 0n) -
      (gasCompensation ?? 0n) -
      (minNetDebt ?? 0n),
    [userLoan?.debtAmount, minNetDebt, gasCompensation],
  )

  const onRepayAmountChange = useCallback(
    (repayAmount: string) => {
      const normalizedLoanAmount = normalizeDecimalNumberInput(repayAmount)
      const trimmedLoan = trimDecimals(normalizedLoanAmount, 2)

      dispatch(setRepayLoanAmount(trimmedLoan))
    },
    [dispatch],
  )

  const isRepayAmountValid = useMemo(() => {
    const amount = floatToBigInt(repayLoanAmount)

    if (
      !userLoan ||
      !amount ||
      amount <= 0 ||
      amount > MUSD.balance ||
      amount >= fullRepaymentAmount ||
      amount > (maxRepaymentAmount ?? 0n)
    )
      return false

    return true
  }, [
    repayLoanAmount,
    userLoan,
    MUSD.balance,
    fullRepaymentAmount,
    maxRepaymentAmount,
  ])

  const hasBalanceError = useMemo(() => {
    const amount = floatToBigInt(repayLoanAmount)

    if (amount > MUSD.balance) return true

    return false
  }, [repayLoanAmount, MUSD.balance])

  const principalRepayAmount = useMemo(() => {
    const amount = floatToBigInt(repayLoanAmount)
    const principalAmount = amount - (userLoan?.interestAmount ?? 0n)
    if (principalAmount <= 0n) return 0n
    return principalAmount
  }, [repayLoanAmount, userLoan?.interestAmount])

  const principalRepayAmountLabel = useMemo(
    () =>
      principalRepayAmount
        ? separateThousandsWithComma(
            bigIntToHumanReadableFormat(
              principalRepayAmount,
              Number(MUSD.decimals),
              6,
            ),
          )
        : "0",
    [MUSD.decimals, principalRepayAmount],
  )

  const interestAmountLabel = useMemo(
    () =>
      bigIntToFloat(userLoan?.interestAmount ?? 0n, Number(MUSD.decimals), 6),
    [MUSD.decimals, userLoan?.interestAmount],
  )

  const debtLeftToRepay = useMemo(
    () => fullRepaymentAmount - principalRepayAmount,
    [fullRepaymentAmount, principalRepayAmount],
  )

  const debtLeftToRepayLabel = useMemo(
    () =>
      debtLeftToRepay
        ? separateThousandsWithComma(
            bigIntToHumanReadableFormat(
              debtLeftToRepay,
              Number(MUSD.decimals),
              6,
            ),
          )
        : "0",
    [debtLeftToRepay, MUSD.decimals],
  )

  const maxRepaymentAmountLabel = useMemo(
    () =>
      maxRepaymentAmount
        ? separateThousandsWithComma(
            bigIntToHumanReadableFormat(
              maxRepaymentAmount,
              Number(MUSD.decimals),
              6,
            ),
          )
        : "0",
    [maxRepaymentAmount, MUSD.decimals],
  )

  return {
    hasBalanceError,
    fullRepaymentAmount,
    maxRepaymentAmountLabel,
    repayLoanAmount,
    principalRepayAmount,
    principalRepayAmountLabel,
    interestAmountLabel,
    onRepayAmountChange,
    isRepayAmountValid,
    prevDebtAmount: userLoan?.debtAmount ?? 0n,
    newDebtAmount: debtLeftToRepay + (gasCompensation ?? 0n),
    debtLeftToRepay,
    debtLeftToRepayLabel,
  }
}

export const useRepayCollateralizationRatio = () => {
  const userLoan = useDappSelector(selectUserLoan)

  const { principalRepayAmount } = useRepayBorrowAmount()

  const {
    collateralizationRatioLabel: prevCollateralizationRatioLabel,
    collateralizationRatio: prevCollateralizationRatio,
  } = useCollateralizationRatioForExistingLoan(
    userLoan?.debtAmount ?? 0n,
    0n,
    userLoan?.collateralAmount ?? 0n,
    0n,
  )

  const {
    collateralizationRatioLabel: newCollateralizationRatioLabel,
    collateralizationRatio: newCollateralizationRatio,
  } = useCollateralizationRatioForExistingLoan(
    userLoan?.debtAmount ?? 0n,
    -principalRepayAmount,
    userLoan?.collateralAmount ?? 0n,
    0n,
  )

  const collateralizationRatioChange = useMemo(
    () =>
      newCollateralizationRatio && prevCollateralizationRatio
        ? newCollateralizationRatio - prevCollateralizationRatio
        : 0n,
    [newCollateralizationRatio, prevCollateralizationRatio],
  )

  const collateralizationRatioChangeLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(collateralizationRatioChange * 100n),
        2,
      ),
    [collateralizationRatioChange],
  )

  return {
    collateralizationRatioChange,
    collateralizationRatioChangeLabel,
    prevCollateralizationRatio,
    prevCollateralizationRatioLabel,
    newCollateralizationRatio,
    newCollateralizationRatioLabel,
  }
}

export const useRepayBorrowTransaction = () => {
  const dispatch = useDappDispatch()

  const { mBTC, MUSD } = useDappSelector(selectWalletBalances)
  const userLoan = useDappSelector(selectUserLoan)

  const {
    emitBorrowRepayStartedEvent,
    emitBorrowRepayErrorEvent,
    emitBorrowRepayEvent,
  } = usePosthogRepayEvent()

  const strategy = useDappSelector(selectRepayStrategy)

  const partialRepaymentStrategy = useDappSelector(selectPartialRepayStrategy)

  const { principalRepayAmount, repayLoanAmount, debtLeftToRepay } =
    useRepayBorrowAmount()

  const { additionalCollateralAmount, updatedCollateral } =
    useUpdatedCollateralBasedOnRatio(
      partialRepaymentStrategy ===
        BorrowPartialRepayStrategy.WITHDRAW_COLLATERAL
        ? -principalRepayAmount
        : 0n,
    )

  const {
    collateralizationRatioChange,
    newCollateralizationRatio,
    prevCollateralizationRatio,
  } = useRepayCollateralizationRatio()

  const { fetchAllBorrowData } = useFetchAllBorrowData()

  const { refreshTokensBalances } = useRefreshPassport()

  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const posthogEventProps: BorrowRepayPosthogEventProps = useMemo(() => {
    const isTotalRepayment = strategy === BorrowRepayStrategy.TOTAL

    const isPartialIncreaseRatio =
      partialRepaymentStrategy === BorrowPartialRepayStrategy.INCREASE_RATIO

    const partialRepaymentStrategyTitle =
      partialRepaymentStrategy === BorrowPartialRepayStrategy.INCREASE_RATIO
        ? "partial_increase_collateralization_ratio"
        : "partial_withdraw_collateral"

    const repaymentStrategyTitle = isTotalRepayment
      ? "total_repayment"
      : partialRepaymentStrategyTitle

    const collateralLeft = isTotalRepayment ? 0n : updatedCollateral

    const collateralChange = isTotalRepayment
      ? updatedCollateral
      : additionalCollateralAmount

    const collRatio = isPartialIncreaseRatio
      ? newCollateralizationRatio ?? 0n
      : prevCollateralizationRatio ?? 0n

    const collRatioChange = isPartialIncreaseRatio
      ? collateralizationRatioChange
      : 0n

    return {
      repayment_strategy: repaymentStrategyTitle,
      collateral: +bigIntToFloat(collateralLeft, Number(mBTC.decimals), 8),
      collateral_change: Math.abs(
        +bigIntToFloat(collateralChange, Number(mBTC.decimals), 8),
      ),
      collateralization_ratio: isTotalRepayment
        ? 0
        : +bigIntToFloat(collRatio, 16, 2),
      collateralization_change: isTotalRepayment
        ? 0
        : +bigIntToFloat(collRatioChange, 16, 2),
      repayment_amount: +repayLoanAmount,
      full_repayment: isTotalRepayment,
      accrued_interest: +bigIntToFloat(
        userLoan?.interestAmount ?? 0n,
        Number(MUSD.decimals),
        2,
      ),
      debt_repaid: +bigIntToFloat(
        principalRepayAmount,
        Number(MUSD.decimals),
        2,
      ),
      remaining_debt: +bigIntToFloat(debtLeftToRepay, Number(MUSD.decimals), 2),
    }
  }, [
    additionalCollateralAmount,
    collateralizationRatioChange,
    debtLeftToRepay,
    mBTC.decimals,
    MUSD.decimals,
    newCollateralizationRatio,
    partialRepaymentStrategy,
    prevCollateralizationRatio,
    principalRepayAmount,
    repayLoanAmount,
    strategy,
    updatedCollateral,
    userLoan?.interestAmount,
  ])

  const repayTransaction = useCallback(async () => {
    const normalizedLoanAmount = floatToBigInt(repayLoanAmount)

    if (strategy === BorrowRepayStrategy.TOTAL) {
      const result = (await dispatch(closeLoan())) as PayloadAction<boolean>

      return result.payload
    }

    // Partial repayment with ratio increase
    if (
      partialRepaymentStrategy === BorrowPartialRepayStrategy.INCREASE_RATIO
    ) {
      const result = (await dispatch(
        repayPartialDebt(normalizedLoanAmount),
      )) as PayloadAction<boolean>

      return result.payload
    }

    // Partial repayment with collateral withdrawal
    const result = (await dispatch(
      repayDebtAndWithdrawCollateral({
        debtChange: normalizedLoanAmount,
        collateralChange: -additionalCollateralAmount,
      }),
    )) as PayloadAction<boolean>

    return result.payload
  }, [
    additionalCollateralAmount,
    dispatch,
    partialRepaymentStrategy,
    repayLoanAmount,
    strategy,
  ])

  const repayHandler = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)

    emitBorrowRepayStartedEvent()

    const success = await repayTransaction()

    if (success) {
      emitBorrowRepayEvent(posthogEventProps)

      await fetchAllBorrowData({ refetch: true })
      await refreshTokensBalances()
    } else {
      emitBorrowRepayErrorEvent(posthogEventProps)
    }

    setIsLoading(false)
    setHasError(!success)
  }, [
    emitBorrowRepayStartedEvent,
    repayTransaction,
    emitBorrowRepayEvent,
    posthogEventProps,
    fetchAllBorrowData,
    refreshTokensBalances,
    emitBorrowRepayErrorEvent,
  ])

  return {
    repayHandler,
    hasError,
    isLoading,
  }
}
